<template>
    <Modal ref="modalRef" :primary-button-disabled="!form.isDirty" :processing="form.processing" max-width="2xl" @submit="submitForm">
        <template #title>
            <asset-short-info :asset="asset" class="my-4">
                <task-group-badge :bg-color="getTaskGroupColor(type)" :group="type" />
            </asset-short-info>
        </template>
        <form class="mt-12" @submit.prevent="submitForm">
            <div
                v-if="
                    type === 'preventative_maintenance' &&
                    canManagePreventativeSchedule &&
                    (hasFeature('schedule_preventative_maintenance_work_orders') || form.output_type === 'work_order')
                "
                class="mb-3"
            >
                <label class="form-label required">{{ trans('quality_assurance.configuration.modal.mode.label') }}</label>
                <segmented-control
                    v-model="form.output_type"
                    :options="[
                        { value: 'task', label: trans('quality_assurance.configuration.modal.mode.task') },
                        { value: 'work_order', label: trans('quality_assurance.configuration.modal.mode.work_order') },
                    ]"
                />
            </div>

            <jet-input
                v-model="form.title"
                :horizontal="false"
                :invalid-feedback="form.errors.title"
                :label="trans('quality_assurance.configuration.modal.title_field.label')"
                :required="true"
            />

            <editor
                v-model="form.description"
                :horizontal="false"
                :invalid-feedback="form.errors.description"
                :label="
                    isWorkOrderMode
                        ? trans('quality_assurance.configuration.modal.maintenance_procedure.label')
                        : trans('quality_assurance.configuration.modal.description.label')
                "
                :required="isWorkOrderMode"
                :rows="3"
                name="description"
            />

            <jet-select
                v-if="!isWorkOrderMode"
                v-model="form.task_id"
                :disabled="availableChecks.data.length <= 1"
                :horizontal="false"
                :invalid-feedback="form.errors.task_id"
                :label="trans('quality_assurance.configuration.modal.task_select.label')"
                :options="availableChecks.data || []"
                :required="true"
            />

            <work-order-sla-fields
                v-if="isWorkOrderMode"
                :can-manage-preventative-schedule="canManagePreventativeSchedule"
                :form="form"
                :selected-sla="selectedSla"
                :sla-options="slaOptions"
            />

            <div class="mt-4 border-t pt-4">
                <h4 class="mb-3 !text-base !font-semibold">{{ trans('quality_assurance.configuration.modal.section.schedule') }}</h4>
                <div class="row">
                    <div class="col-6">
                        <jet-date-picker
                            :horizontal="false"
                            :invalid-feedback="form.errors.start_date"
                            :label="trans('quality_assurance.configuration.modal.starts.date.label')"
                            :model-value="form.start_date"
                            :required="true"
                            @update:model-value="onStartDateUpdate"
                        />
                    </div>
                    <div class="col-3">
                        <time-picker
                            v-model="form.start_date"
                            :disabled="!form.start_date"
                            :horizontal="false"
                            :label="trans('quality_assurance.configuration.modal.from.label')"
                            :required="true"
                            step="5"
                        />
                    </div>
                </div>
                <deadline-field v-model="form.end_date" :invalid-feedback="form.errors.end_date" :start-date="form.start_date" />
                <recurrence-rule
                    v-model="form.recurrence"
                    :form="form"
                    :horizontal="false"
                    :invalid-feedback="form.errors.recurrence"
                    :options="recurrenceOptions"
                    :required="true"
                />
                <small v-if="form.recurrence.repeat !== 'once'" class="-mt-1 block text-muted">
                    {{ trans('quality_assurance.configuration.modal.section.schedule.series_hint') }}
                </small>
            </div>

            <!-- SLA configs express their end via the contract term above; only
                 non-SLA recurring schedules need the explicit stop condition. -->
            <div v-if="form.recurrence.repeat !== 'once' && !selectedSla" class="mt-2">
                <h4 class="mb-3 !text-base !font-semibold">{{ trans('quality_assurance.configuration.modal.section.ends') }}</h4>
                <recurrence-end-condition v-model="form.recurrence" :form="form" :min-end-date="form.start_date" />
            </div>

            <div v-if="summaryLines.length" class="mt-4 rounded-xl bg-slate-50 p-3 text-sm">
                <h4 class="mb-2 !text-base !font-semibold">{{ trans('quality_assurance.configuration.modal.summary.heading') }}</h4>
                <p
                    v-for="line in summaryLines"
                    :key="line.id"
                    :class="[line.tone === 'warning' ? 'text-warning' : 'text-muted', { 'ml-4': line.bullet }]"
                    class="mb-1"
                >
                    {{ line.bullet ? `• ${line.text}` : line.text }}
                </p>
            </div>

            <task-options-fields
                v-if="!isWorkOrderMode"
                :alert-options="alertOptions"
                :form="form"
                :should-show-alert-dropdown="shouldShowAlertDropdown"
            />

            <work-order-notification-fields
                v-if="isWorkOrderMode"
                :available-airport-contacts="availableAirportContacts"
                :form="form"
                :vendor-contact-tree="vendorContactTree"
            />

            <jet-checkbox
                v-if="form.recurrence.repeat !== 'once' && form.recurrence.repeat !== 'never'"
                v-model="form.is_paused"
                :invalid-feedback="form.errors.is_paused"
                :label="trans('quality_assurance.configuration.modal.pause.label')"
            />
        </form>
    </Modal>
</template>

<script setup>
import JetSelect from '@/Jetstream/Select.vue';
import { getSelectionState } from '@/composables/useHierarchicalLocationDisplay.ts';
import SegmentedControl from '@/Jetstream/SegmentedControl.vue';
import JetInput from '@/Jetstream/Input.vue';
import JetDatePicker from '@/Jetstream/Datepicker.vue';
import Editor from '@/Jetstream/Editor.vue';
import JetCheckbox from '@/Jetstream/Checkbox.vue';
import { useForm, usePage } from '@inertiajs/vue3';
import { computed, ref, watch } from 'vue';
import TimePicker from '@/Components/TimePicker.vue';
import { trans } from '@/mixins/i18n.js';
import DeadlineField from '@/Components/DeadlineField.vue';
import RecurrenceRule from '@/Components/RecurrenceRule.vue';
import RecurrenceEndCondition from '@/Components/RecurrenceEndCondition.vue';
import { useScheduleSummary } from '@/composables/useScheduleSummary';
import { DateTime } from 'luxon';
import AssetShortInfo from '@/Components/AssetShortInfo.vue';
import TaskGroupBadge from '@/Components/TaskGroupBadge.vue';
import { getTaskGroupColor } from '@/Pages/TaskList/utils.js';
import { hasFeature } from '@/mixins/hasFeature.js';
import { hasAnyPermission } from '@/mixins/hasAnyPermission';
import WorkOrderSlaFields from './QualityAssuranceConfigurationModal/WorkOrderSlaFields.vue';
import WorkOrderNotificationFields from './QualityAssuranceConfigurationModal/WorkOrderNotificationFields.vue';
import TaskOptionsFields from './QualityAssuranceConfigurationModal/TaskOptionsFields.vue';

const modalRef = ref(null);
const emit = defineEmits(['close', 'saved']);

const props = defineProps({
    asset: {
        type: Object,
        required: true,
    },
    title: {
        type: String,
        required: true,
    },
    show: {
        type: Boolean,
        default: false,
    },
    availableChecks: {
        type: Object,
        required: true,
    },
    availableAssignees: {
        type: Object,
        required: true,
    },
    recurrence: {
        type: Object,
        required: true,
    },
    alertOptions: {
        type: Object,
        required: false,
    },
    configuration: {
        type: Object,
        required: false,
    },
    type: {
        type: String,
        required: true,
    },
    availableAirportContacts: {
        type: Object,
        required: false,
        default: () => ({ data: [] }),
    },
    availableVendorContacts: {
        type: Array,
        required: false,
        default: () => [],
    },
    availableSlas: {
        type: Array,
        required: false,
        default: () => [],
    },
});

const page = usePage();

function parseDateString(dateString) {
    const date = DateTime.fromISO(dateString);
    if (!date.isValid) {
        return undefined;
    }
    return date;
}

const getDefaultAlertForFrequency = (frequency) => {
    switch (frequency) {
        case 'monthly':
            return '1_week_before';
        case 'yearly':
            return '1_month_before';
        default:
            return 'none';
    }
};

// PM work orders must repeat weekly or less often — once/hourly/daily aren't
// meaningful for a physically dispatched work order. Task-mode "scheduling
// tasks" configs keep the full list.
const SUB_WEEKLY_FREQUENCIES = ['once', 'hourly', 'daily'];

// Bump a sub-weekly selection up to the weekly minimum.
function normalizeWorkOrderRecurrence(recurrence) {
    if (!SUB_WEEKLY_FREQUENCIES.includes(recurrence.repeat)) {
        return { ...recurrence };
    }
    return { ...recurrence, repeat: 'weekly', interval: 1, weekdays: [] };
}

// Restrict prefilled selections to contacts the picker can still render: a
// previously-saved user who lost access, or a maintainer unassigned from the
// asset, would otherwise count as a hidden, unremovable chip and 422 on save.
const availableAirportContactIds = new Set((props.availableAirportContacts?.data ?? []).map((contact) => contact.id));

// When the config is already linked to an SLA, vendor contacts are scoped to that
// SLA's maintainer — keep the prefilled selection within that scope too.
const initialSlaMaintainerId =
    props.availableSlas.find((sla) => sla.id === (props.configuration?.data?.maintainerServiceLevelAgreementId ?? null))?.maintainerId ?? null;

// The hierarchical vendor picker works in node ids: "m:<maintainer uuid>" for a
// whole maintainer, "c:<channel id>" for a single contact. Rebuild the initial
// selection from the stored whole-maintainer ids + individual channel ids,
// dropping anything no longer renderable (unassigned maintainer / removed contact
// / out-of-SLA-scope) so a stale hidden node can't 422 on save.
const scopedInitialMaintainers = (props.availableVendorContacts ?? []).filter(
    (maintainer) => initialSlaMaintainerId === null || maintainer.maintainerId === initialSlaMaintainerId,
);
const validMaintainerNodeIds = new Set(scopedInitialMaintainers.map((maintainer) => maintainer.id));
const validChannelNodeIds = new Set(scopedInitialMaintainers.flatMap((maintainer) => (maintainer.children ?? []).map((contact) => contact.id)));

const intendedVendorSelection = [
    ...(props.configuration?.data?.notifications?.vendorContactIds || [])
        .map((id) => `m:${id}`)
        .filter((nodeId) => validMaintainerNodeIds.has(nodeId)),
    ...(props.configuration?.data?.notifications?.vendorContactChannelIds || [])
        .map((id) => `c:${id}`)
        .filter((nodeId) => validChannelNodeIds.has(nodeId)),
];

// Pre-expand to the same node set the tree normalizes to on open (a whole
// maintainer expands to all its leaves; a parent folds back in once every leaf is
// selected), so simply opening the modal doesn't register the form as dirty.
const expandVendorSelection = (intended) => {
    const result = new Set();

    for (const maintainer of scopedInitialMaintainers) {
        const childNodeIds = (maintainer.children ?? []).map((contact) => contact.id);

        if (intended.includes(maintainer.id)) {
            result.add(maintainer.id);
            childNodeIds.forEach((id) => result.add(id));
            continue;
        }

        const selectedChildNodeIds = childNodeIds.filter((id) => intended.includes(id));
        selectedChildNodeIds.forEach((id) => result.add(id));

        if (childNodeIds.length > 0 && selectedChildNodeIds.length === childNodeIds.length) {
            result.add(maintainer.id);
        }
    }

    return [...result];
};

const initialVendorSelection = expandVendorSelection(intendedVendorSelection);

const initialRecurrence = {
    repeat: props.configuration?.data?.recurrence?.repeat || 'once',
    interval: props.configuration?.data?.recurrence?.interval || 1,
    weekdays: props.configuration?.data?.recurrence?.weekdays || [],
    stopCondition: props.configuration?.data?.recurrence?.stopCondition || 'never',
    count: props.configuration?.data?.recurrence?.count || 1,
    until: parseDateString(props.configuration?.data?.recurrence?.until),
};

const form = useForm({
    type: props.configuration?.data?.type || props.type,
    output_type: props.configuration?.data?.outputType ?? 'task',
    task_id: props.configuration?.data?.configId || undefined,
    title: props.configuration?.data?.title || undefined,
    description: props.configuration?.data?.description || undefined,
    asset_id: props.asset.id,
    start_date: parseDateString(props.configuration?.data?.startDate),
    end_date: parseDateString(props.configuration?.data?.endDate),
    // SLA contract term (date-only strings); only user-set when the linked SLA
    // carries no agreement dates, otherwise display-only.
    term_start: props.configuration?.data?.termStart ?? null,
    term_end: props.configuration?.data?.termEnd ?? null,
    assignee_ids: props.configuration?.data?.assigneeIds || undefined,
    // Legacy work-order configs can still carry a sub-weekly repeat from before
    // this restriction existed — normalize on load, not just on mode switch, so
    // the form can't render a value invalid for a save it never touched.
    recurrence: props.configuration?.data?.outputType === 'work_order' ? normalizeWorkOrderRecurrence(initialRecurrence) : initialRecurrence,
    alert_at: props.configuration?.data?.alertAt,
    is_paused: props.configuration?.data?.isPaused ?? false,
    requires_approval: props.configuration?.data?.requiresApproval ?? false,
    requires_presence_detection: props.configuration?.data?.requiresPresenceDetection ?? false,
    notification_airport_contact_ids: (props.configuration?.data?.notifications?.airportContactIds || []).filter((id) =>
        availableAirportContactIds.has(id),
    ),
    // Flat array of vendor-picker node ids ("m:<uuid>" / "c:<channel id>"); split
    // into notification_vendor_contact_ids + notification_vendor_contact_channel_ids
    // on submit (see submitForm).
    vendor_selection: initialVendorSelection,
    notification_lead_time_weeks: props.configuration?.data?.notifications?.leadTimeWeeks ?? 4,
    maintainer_service_level_agreement_id: props.configuration?.data?.maintainerServiceLevelAgreementId ?? null,
});

const selectedSla = computed(() => props.availableSlas.find((sla) => sla.id === form.maintainer_service_level_agreement_id) ?? null);

// Vendor contacts are the asset's maintainers expanded into a maintainer →
// business/support contact tree (a leaf is one contact's email). Once an SLA is
// linked the schedule belongs to that SLA's maintainer, so the tree offers only
// that maintainer's branch. The backend mirrors this scope on save.
const vendorContactTree = computed(() => {
    const slaMaintainerId = selectedSla.value?.maintainerId ?? null;

    return (props.availableVendorContacts ?? [])
        .filter((maintainer) => slaMaintainerId === null || maintainer.maintainerId === slaMaintainerId)
        .map((maintainer) => ({
            id: maintainer.id,
            maintainerId: maintainer.maintainerId,
            name: maintainer.name,
            children: (maintainer.children ?? []).map((contact) => ({
                id: contact.id,
                channelId: contact.channelId,
                name: contact.name,
                email: contact.email,
                subtitle: contact.email,
                badge:
                    contact.type === 'business'
                        ? trans('quality_assurance.configuration.modal.notifications.vendor_contacts.business')
                        : trans('quality_assurance.configuration.modal.notifications.vendor_contacts.support'),
            })),
        }));
});

// When the user switches the linked SLA, drop any selected vendor node the new
// SLA no longer permits, so a stale, hidden node can't 422 on save. The initial
// selection is already SLA-scoped above, so this only reacts to user changes.
watch(
    () => selectedSla.value?.maintainerId ?? null,
    () => {
        const allowed = new Set(
            vendorContactTree.value.flatMap((maintainer) => [maintainer.id, ...maintainer.children.map((contact) => contact.id)]),
        );
        form.vendor_selection = form.vendor_selection.filter((id) => allowed.has(id));
    },
);

const linkedSlaId = props.configuration?.data?.maintainerServiceLevelAgreementId ?? null;

const slaOptions = computed(() =>
    props.availableSlas
        // Expired SLAs are not selectable for new links (the server rejects
        // them); keep the one this config is already linked to so an existing
        // schedule remains editable.
        .filter((sla) => !sla.expired || sla.id === linkedSlaId)
        .map((sla) => ({
            value: sla.id,
            label:
                `${sla.maintainerName} · ${sla.typeLabel}` + (sla.expired ? ` — ${trans('quality_assurance.configuration.modal.sla.expired')}` : ''),
        })),
);

// The whole preventative-maintenance scheduling feature is gated behind a
// dedicated manage permission; without it the SLA picker is read-only (and the
// preventative section is hidden upstream, so the modal won't open at all).
const canManagePreventativeSchedule = hasAnyPermission(['quality_assurance.preventative_maintenance_schedule.manage']);

const { lines: summaryLines } = useScheduleSummary({
    recurrence: () => form.recurrence,
    startDate: () => form.start_date,
    endDate: () => form.end_date,
    isPaused: () => form.is_paused,
    slaEndDate: () => selectedSla.value?.endDate ?? null,
    locale: () => page.props.locale,
    isWorkOrder: () => isWorkOrderMode.value,
});

function formatDate(value) {
    if (!value) {
        return '';
    }
    const parsed = DateTime.fromISO(value);
    return parsed.isValid ? parsed.toFormat('yyyy-MM-dd') : value;
}

// Show alert dropdown only for broader performance windows
const shouldShowAlertDropdown = computed(() => {
    const frequency = form.recurrence.repeat;
    return ['weekly', 'monthly', 'yearly'].includes(frequency);
});

const isWorkOrderMode = computed(() => form.output_type === 'work_order');

const recurrenceOptions = computed(() =>
    isWorkOrderMode.value ? props.recurrence.data.filter((option) => !SUB_WEEKLY_FREQUENCIES.includes(option.value)) : props.recurrence.data,
);

// Watch for frequency changes and set defaults for new configurations
watch(
    () => form.recurrence.repeat,
    (newFrequency, oldFrequency) => {
        const isNewConfiguration = !props.configuration?.data?.id;
        const currentlyShowsAlerts = ['weekly', 'monthly', 'yearly'].includes(newFrequency);
        const previouslyShowedAlerts = oldFrequency && ['weekly', 'monthly', 'yearly'].includes(oldFrequency);

        if (isNewConfiguration && currentlyShowsAlerts && !previouslyShowedAlerts) {
            form.alert_at = getDefaultAlertForFrequency(newFrequency);
        }

        if (isNewConfiguration && currentlyShowsAlerts && previouslyShowedAlerts) {
            const currentDefault = getDefaultAlertForFrequency(oldFrequency);
            if (form.alert_at === currentDefault) {
                form.alert_at = getDefaultAlertForFrequency(newFrequency);
            }
        }
    },
);

// Recurrence as it stood before entering work-order mode, so switching back to
// task mode can undo the sub-weekly bump below instead of leaving a "New Test
// Record" stuck showing the work-order schedule's Repeat/Ends fields.
let recurrenceBeforeWorkOrder = null;

watch(
    () => form.output_type,
    (newMode) => {
        if (newMode === 'work_order') {
            form.task_id = undefined;
            recurrenceBeforeWorkOrder = form.recurrence;
            form.recurrence = normalizeWorkOrderRecurrence(form.recurrence);
            return;
        }

        if (recurrenceBeforeWorkOrder) {
            form.recurrence = recurrenceBeforeWorkOrder;
            recurrenceBeforeWorkOrder = null;
        }

        form.maintainer_service_level_agreement_id = null;

        // Notifications only apply to work orders; drop any selections so a
        // task-mode save doesn't carry stale (and now unvalidated) contacts.
        form.notification_airport_contact_ids = [];
        form.vendor_selection = [];
    },
);

// When an SLA is selected, prefill an empty start date with today, clamped
// into the SLA window — the backend requires the start date to fall on/after
// the SLA start and on/before its end (validateStartDateInSlaWindow). So an
// active SLA prefills today, while one that hasn't started yet falls back to
// the SLA start. The end_date is intentionally left untouched — it's the
// fulfillment window for the first occurrence and depends on the recurrence
// the user picks next.
watch(
    () => form.maintainer_service_level_agreement_id,
    () => {
        const sla = selectedSla.value;
        if (!sla || form.start_date) {
            return;
        }

        const slaStart = sla.startDate ? DateTime.fromISO(sla.startDate).startOf('day') : null;
        const slaEnd = sla.endDate ? DateTime.fromISO(sla.endDate).endOf('day') : null;
        const now = DateTime.now();

        // Whole SLA window already in the past — prefilling would clamp to the
        // (past) end and silently create an overdue schedule, so leave the start
        // empty and let the user choose.
        if (slaEnd?.isValid && slaEnd < now) {
            return;
        }

        let candidate = now.startOf('day');
        if (slaStart?.isValid && candidate < slaStart) {
            candidate = slaStart;
        }

        form.start_date = candidate.set({ hour: 0, minute: 0 });
    },
);

// The cadence anchor can't begin before its contract term, so when the user
// sets a term start later than "Starting from", pull "Starting from" up to it
// (keeping the chosen time). Only bumps forward; an earlier term start leaves
// the anchor alone.
watch(
    () => form.term_start,
    (value) => {
        if (!value) {
            return;
        }

        const termStart = DateTime.fromISO(value);
        if (!termStart.isValid) {
            return;
        }

        const current = form.start_date;
        if (current?.isValid && termStart.startOf('day') <= current.startOf('day')) {
            return;
        }

        form.start_date = current?.isValid ? termStart.set({ hour: current.hour, minute: current.minute }) : termStart;
    },
);

const onStartDateUpdate = (date) => {
    const parsed = parseDateString(date);
    if (!parsed) {
        form.start_date = undefined;
        return;
    }

    // Keep the previously chosen time when only the date changes.
    form.start_date = form.start_date?.isValid ? parsed.set({ hour: form.start_date.hour, minute: form.start_date.minute }) : parsed;
};

const submitForm = () => {
    const url = props.configuration?.data?.id
        ? route('quality-assurance.configurations.update', { configuration: props.configuration.data.id })
        : route('quality-assurance.configurations.store', {
              assetId: props.asset.id,
              type: form.type,
          });

    form.transform((data) => {
        data._method = props.configuration?.data?.id ? 'PUT' : 'POST';
        data.end_date = data.end_date?.toFormat("yyyy-MM-dd'T'HH:mm");
        data.start_date = data.start_date?.toFormat("yyyy-MM-dd'T'HH:mm");

        data.recurrence = {
            ...(data.recurrence.repeat === 'once'
                ? {
                      repeat: data.recurrence.repeat,
                  }
                : {
                      repeat: data.recurrence.repeat,
                      interval: data.recurrence.interval,
                      stopCondition: data.recurrence.stopCondition,
                      ...(data.recurrence.repeat === 'weekly' ? { weekdays: data.recurrence.weekdays } : {}),
                      ...(data.recurrence.stopCondition === 'count' ? { count: data.recurrence.count } : {}),
                      ...(data.recurrence.stopCondition === 'until' ? { until: parseDateString(data.recurrence.until)?.toFormat('yyyy-MM-dd') } : {}),
                  }),
        };

        if (data.recurrence.repeat === 'once' || data.recurrence.repeat === 'never') {
            delete data.is_paused;
        }

        // The contract term only applies to SLA-linked work orders; drop it
        // otherwise so the backend's prohibited rule is satisfied. When the SLA
        // itself carries a bound the field is read-only, so drop that side too
        // rather than persisting a stale config-local value that could become an
        // active fallback if the SLA's dates are later removed.
        if (data.output_type !== 'work_order' || !data.maintainer_service_level_agreement_id) {
            delete data.term_start;
            delete data.term_end;
        } else {
            const sla = selectedSla.value;
            if (sla?.startDate) {
                delete data.term_start;
            }
            if (sla?.endDate) {
                delete data.term_end;
            }
        }

        // Split the vendor tree selection into the two persisted shapes: a whole
        // maintainer (all of its email contacts, incl. future ones) is sent as the
        // maintainer id; a partial selection is sent as the picked contacts'
        // channel ids. Mirrors the backend's vendor_contact_ids /
        // vendor_contact_channel_ids storage.
        const selection = data.vendor_selection || [];
        const vendorContactIds = [];
        const vendorContactChannelIds = [];

        for (const maintainer of vendorContactTree.value) {
            const state = getSelectionState(maintainer, selection);

            if (state === 'selected') {
                vendorContactIds.push(maintainer.maintainerId);
            } else if (state === 'indeterminate') {
                for (const contact of maintainer.children) {
                    if (selection.includes(contact.id)) {
                        vendorContactChannelIds.push(contact.channelId);
                    }
                }
            }
        }

        data.notification_vendor_contact_ids = vendorContactIds;
        data.notification_vendor_contact_channel_ids = vendorContactChannelIds;
        delete data.vendor_selection;

        return data;
    }).post(url, {
        preserveScroll: true,
        onSuccess: () => {
            modalRef.value?.close();
        },
    });
};
</script>
